import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import LotteryCodeField from '../../components/LotteryCodeField';
import DateField from '../../components/DateField';
import BankScopeField from '../../components/BankScopeField';
import ScopeField from '../../components/ScopeField';
import { fetchPrizedNumbers } from '../../api/prizedNumbers';
import { todayISO } from '../../utils/date';
import './PrizedNumbersPage.css';

const fmtMoney = (v) => Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const scopeApiFilter = (filter) => {
  if (filter === 'Groupe') return 'Group';
  if (filter === 'Superviseur') return 'Supervisor';
  if (filter === 'Banque') return 'Bank';
  return 'All';
};

const PrizedNumbersPage = () => {
  const [filter, setFilter] = useState('Tout');
  const [scopeId, setScopeId] = useState('');
  const [scopeName, setScopeName] = useState('');
  const [date, setDate] = useState(todayISO());
  const [lottery, setLottery] = useState('');
  const [lotteryName, setLotteryName] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!lottery) {
      setError('Choisis une loterie.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await fetchPrizedNumbers({
        tirage: lottery,
        date,
        filter: scopeApiFilter(filter),
        scope: filter === 'Tout' ? undefined : scopeId,
      });
      setResult(data);
    } catch (err) {
      setError('Recherche impossible.');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <p className="pg-breadcrumb">
        Ruta : <Link to="/dashboard" className="bc-link">Menu</Link>
        {' >> '}
        <Link to="/rapports" className="bc-link">Reports</Link>
        {' >> '}
        <span className="bc-current">Prized Numbers</span>
      </p>

      <div className="pg-card">
        <div className="pg-card-header">
          <span className="pg-card-title">Prized Numbers</span>
        </div>
        <div className="pg-card-body">
          <div className="pg-radio-row">
            {['Tout', 'Groupe', 'Superviseur', 'Banque'].map((opt) => (
              <label key={opt} className="pg-radio-label">
                <input
                  type="radio"
                  name="filter"
                  value={opt}
                  checked={filter === opt}
                  onChange={() => { setFilter(opt); setScopeId(''); setScopeName(''); }}
                />
                {opt}
              </label>
            ))}
          </div>

          {filter !== 'Tout' && (
            <div className="pg-row pg-scope-row">
              <label className="pg-label">{filter}</label>
              {filter === 'Banque' ? (
                <BankScopeField
                  id={scopeId}
                  name={scopeName}
                  onResolve={(id, name) => { setScopeId(id); setScopeName(name); }}
                  inputClass="pg-input pg-input-sm"
                  iconClass="pg-icon-btn"
                  nameClass="pg-copy-name"
                  errClass="pg-lookup-err"
                />
              ) : (
                <ScopeField
                  filter={filter}
                  id={scopeId}
                  name={scopeName}
                  onResolve={(id, nm) => { setScopeId(id); setScopeName(nm); }}
                  inputClass="pg-input pg-input-sm"
                  iconClass="pg-icon-btn"
                  nameClass="pg-copy-name"
                  errClass="pg-lookup-err"
                />
              )}
            </div>
          )}

          <form onSubmit={handleSearch} className="pg-form">
            <div className="pg-row">
              <label className="pg-label">Date</label>
              <DateField className="pg-input pg-input-date" value={date} onChange={setDate} />
            </div>

            <div className="pg-row">
              <label className="pg-label">Loterie</label>
              <LotteryCodeField
                value={lottery}
                name={lotteryName}
                onChange={setLottery}
                onResolve={(code, name) => { setLottery(code); setLotteryName(name); }}
                allowAll={false}
                inputClass="pg-input pg-input-sm"
                iconClass="pg-icon-btn"
                nameClass="pg-copy-name"
              />
            </div>

            {error && <p className="pg-error">{error}</p>}

            <button type="submit" className="pg-search-btn" disabled={loading}>
              {loading ? 'Recherche...' : 'Chercher'}
            </button>
          </form>
        </div>
      </div>

      {result && (
        <div className="pg-card pg-results">
          <div className="pg-card-header">
            <span className="pg-card-title">
              Prized Numbers - {new Date(`${result.date}T00:00:00`).toLocaleDateString('fr-FR')}
            </span>
          </div>
          <div className="pg-card-body">
            <p className="pg-copy-name">{result.tirage_code} - {result.tirage_name}</p>

            {!result.has_result && (
              <p className="pg-empty">Aucun numéro gagnant saisi pour ce tirage à cette date.</p>
            )}

            {result.has_result && result.sections.length === 0 && (
              <p className="pg-empty">Aucun pari sur les numéros gagnants de ce tirage (sur cette portée).</p>
            )}

            {result.sections.map((section) => (
              <div key={section.product_abbr} className="pg-section">
                <p className="pg-section-title">{section.product_name}</p>
                <table className="pg-table">
                  <thead>
                    <tr>
                      <th>No.</th>
                      <th>Points</th>
                      <th>Vendu</th>
                      <th>Gagne</th>
                      <th>Match</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section.rows.map((row) => (
                      <tr key={`${row.numero}-${row.match}`}>
                        <td className="pg-num">{row.numero}</td>
                        <td>{row.points}</td>
                        <td className="pg-money">{fmtMoney(row.vendu)}</td>
                        <td className="pg-money">{fmtMoney(row.gagne)}</td>
                        <td>{row.match}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}

            {result.has_result && result.sections.length > 0 && (
              <div className="pg-totals">
                <div className="pg-totals-row">
                  <span className="pg-totals-label">Ventes Brutes</span>
                  <span className="pg-totals-value">{fmtMoney(result.ventes_brutes)}</span>
                </div>
                <div className="pg-totals-row">
                  <span className="pg-totals-label">Total des gains</span>
                  <span className="pg-totals-value">{fmtMoney(result.total_gains)}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
};

export default PrizedNumbersPage;
